import CSSModules from 'react-css-modules'
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import TagManager from 'react-gtm-module'
import { emailRegexp } from 'javascripts/common/regexp'
import { API } from 'javascripts/api'
import ClientLoginLayout from 'spa/client/layouts/ClientLoginLayout'
import styles from './style.module.scss'

interface LocationState {
  backTo?: string;
}

const ClientLogin: React.FC = CSSModules<React.FC>(() => {
  const [email, setEmail] = useState<string>('')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [validEmail, setValidEmail] = useState<boolean>(false)
  const [password, setPassword] = useState<string>('')
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [validPassword, setValidPassword] = useState<boolean>(false)
  const [waiting, setWaiting] = useState<boolean>(false)
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const homePath = '/clients/home'

  useEffect(() => {
    const valid = emailRegexp.test(email)
    setValidEmail(valid)

    if (valid || (email ?? '') === '') {
      setEmailError(null)
    }
    else {
      setEmailError(t('clientAuth.general.email.invalid'))
    }
  }, [email])

  useEffect(() => {
    if (password === '') {
      setValidPassword(false)
    }
    else {
      setValidPassword(true)
    }
    setPasswordError(null)
  }, [password])

  const redirectBack = (state?: object) => {
    const redirectBackUrl = searchParams.get('back_to')
    if (redirectBackUrl) {
      window.location.href = redirectBackUrl
      return
    }

    const locationState = location.state as LocationState
    navigate(locationState?.backTo ?? homePath, { state })
  }

  const handleEmailChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    event.preventDefault()

    setEmail(event.currentTarget.value)
  }

  const handlePasswordChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    event.preventDefault()

    setPassword(event.currentTarget.value)
  }

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault()

    if (waiting) return
    setWaiting(true)

    API.clientLogin({ email, password }).then(response => {
      if (response.status === 200) {
        TagManager.dataLayer({
          dataLayer: {
            clientStaffId: response.data.id,
          },
          dataLayerName: 'ClientDataLayer'
        })
        setWaiting(false)
        redirectBack({
          flash: { success: response.data.message }
        })
      }
    }).catch(error => {
      if (API.isAxiosError(error) && API.isUnauthorizedError(error.response)) {
        setPasswordError(error.response.data.error)
        setWaiting(false)
      }
    })
  }

  const disabled = () => (waiting || !validEmail || !validPassword)

  return (
    <ClientLoginLayout>
      <div styleName='client-login'>
        <form onSubmit={handleSubmit}>
          <h1>ログイン</h1>
            <div styleName='form-item'>
            <label styleName='form-item-label' htmlFor='input-email'>メールアドレス</label>
            <div styleName='form-item-inner'>
              <div styleName='form-item-content'>
                <input type='email' id='input-email' className={emailError ? '-error' : ''} value={email} onChange={handleEmailChange} />
              </div>
              {emailError && <div styleName='form-item-error'>{emailError}</div>}
            </div>
          </div>
          <div styleName='form-item'>
            <label styleName='form-item-label' htmlFor='input-password'>パスワード</label>
            <div styleName='form-item-inner'>
              <div styleName='form-item-content'>
                <input type='password' id='input-password' className={passwordError ? '-error' : ''} value={password} onChange={handlePasswordChange} />
              </div>
              {passwordError && <div styleName='form-item-error'>{passwordError}</div>}
            </div>
          </div>
          <div styleName='form-item'>
            <div styleName='form-item-inner'>
              <div styleName='form-item-content'>
                <input type='submit' value='送信する' disabled={disabled()} className={disabled() ? 'disabled' : ''} />
              </div>
            </div>
          </div>
          <div styleName='item-link'>
            <Link to='/client_auth/signup'>まだアカウントをお持ちでない方</Link>
          </div>
          <div styleName='item-link'>
            <Link to='/client_auth/signup'>パスワード再設定</Link>
          </div>
        </form>
      </div>
      <div className='api-state' styleName='api-state'>
        {waiting && <div className='api-waiting' />}
      </div>
    </ClientLoginLayout>
  )
}, styles)

export default ClientLogin
